const API_BASE = process.env.REACT_APP_BACKEND_URL;
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const CONSENT_KEY = "viscar-cookie-consent-v1";
const SESSION_KEY = "viscar-analytics-session-v1";

const getSessionId = () => {
  const existing = window.localStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const created = `session-${Math.random().toString(36).slice(2, 12)}`;
  window.localStorage.setItem(SESSION_KEY, created);
  return created;
};

export const getConsentSettings = () => {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const saveConsentSettings = (value) => {
  window.localStorage.setItem(CONSENT_KEY, JSON.stringify(value));
};

const ensureGtag = () => {
  if (!GA_MEASUREMENT_ID || typeof window === "undefined") return false;
  if (window.gtag) return true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = (...args) => window.dataLayer.push(args);

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.gtag("js", new Date());
  window.gtag("consent", "default", { analytics_storage: "denied", ad_storage: "denied" });
  window.gtag("config", GA_MEASUREMENT_ID, { anonymize_ip: true, send_page_view: false });
  return true;
};

export const syncAnalyticsConsent = (enabled) => {
  if (!GA_MEASUREMENT_ID) return;
  ensureGtag();
  window.gtag?.("consent", "update", {
    analytics_storage: enabled ? "granted" : "denied",
    ad_storage: "denied",
  });
};

const getAttribution = () => {
  const url = new URL(window.location.href);
  return {
    referrer: document.referrer || "",
    utm_source: url.searchParams.get("utm_source") || "",
    utm_medium: url.searchParams.get("utm_medium") || "",
    utm_campaign: url.searchParams.get("utm_campaign") || "",
    utm_content: url.searchParams.get("utm_content") || "",
  };
};

const getDeviceType = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/ipad|tablet/.test(userAgent)) return "tablet";
  if (/mobi|android|iphone/.test(userAgent)) return "mobile";
  return "desktop";
};

const isInternalTraffic = () => {
  const host = window.location.hostname;
  return host.includes("preview.emergentagent.com") || host.includes("localhost") || host.includes("127.0.0.1");
};

export const recordAnalyticsEvent = async ({ eventType, pagePath, pageTitle = "", target = "", sourceLabel = "", metadata = {} }) => {
  const consent = getConsentSettings();
  const analyticsConsent = Boolean(consent?.analytics);
  const attribution = getAttribution();
  const payload = {
    event_type: eventType,
    page_path: pagePath,
    page_title: pageTitle,
    target,
    source_label: sourceLabel,
    session_id: getSessionId(),
    consent_analytics: analyticsConsent,
    metadata: {
      ...metadata,
      device_type: getDeviceType(),
      is_internal: isInternalTraffic() ? "true" : "false",
    },
    ...attribution,
  };

  if (analyticsConsent && GA_MEASUREMENT_ID) {
    ensureGtag();
    const nameMap = {
      page_view: "page_view",
      phone_click: "phone_click",
      whatsapp_click: "whatsapp_click",
      contact_request: "contact_request",
    };
    window.gtag?.("event", nameMap[eventType] || eventType, {
      page_path: pagePath,
      page_title: pageTitle || document.title,
      target,
      source_label: sourceLabel,
    });
  }

  await fetch(`${API_BASE}/api/analytics/events`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).catch(() => null);
};

export const submitContactIntent = async (payload) => {
  const attribution = getAttribution();
  await fetch(`${API_BASE}/api/contact-intents`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...payload,
      session_id: getSessionId(),
      metadata: {
        device_type: getDeviceType(),
        is_internal: isInternalTraffic() ? "true" : "false",
      },
      ...attribution,
    }),
  }).catch(() => null);
};